import { format } from 'date-fns'
import _ from 'lodash'
import moment from 'moment'

import COLORS from 'pared/constants/colors'

import { getRevenueCenterMetricDisplayName } from './locationIntradaySelectOptions'

export interface IColumn {
  key: string
  title: string
  sortable?: boolean
  decimal?: number
  background?: {
    colors: string[]
    range: number[]
    value: string
  }
}

interface ITableColumn {
  key: string
  title: string
  align: string
  background: string | undefined
  children: IColumn[]
}

function getHourLabel(hourStr: number) {
  const hourString = String(hourStr)
  const paddedHour = hourString.padStart(4, '0')

  try {
    const hours = parseInt(paddedHour.substring(0, 2), 10)
    const minutes = parseInt(paddedHour.substring(2, 4), 10)

    const date = new Date(0)
    date.setHours(hours, minutes, 0, 0)
    return format(date, 'haaa')
  } catch (error) {
    return String(hourStr)
  }
}

function getDayOfWeekLabel(
  dayOfWeek: number,
  startDate: moment.Moment,
  endDate: moment.Moment,
) {
  const dayOfWeekToDateStrMap: { [dayOfWeek: number]: string } = {}
  if (
    startDate.isValid() &&
    endDate.isValid() &&
    startDate.clone().add(7, 'days').isAfter(endDate)
  ) {
    const dateIter = startDate.clone()
    while (dateIter.isSameOrBefore(endDate)) {
      let dowIndex = dateIter.day()
      dowIndex = dowIndex === 0 ? 7 : dowIndex
      dayOfWeekToDateStrMap[dowIndex] = dateIter.format('M/D')
      dateIter.add(1, 'day')
    }
  }

  let dayOfWeekStr = ''
  if (dayOfWeek === 1) {
    dayOfWeekStr = 'Monday'
  } else if (dayOfWeek === 2) {
    dayOfWeekStr = 'Tuesday'
  } else if (dayOfWeek === 3) {
    dayOfWeekStr = 'Wednesday'
  } else if (dayOfWeek === 4) {
    dayOfWeekStr = 'Thursday'
  } else if (dayOfWeek === 5) {
    dayOfWeekStr = 'Friday'
  } else if (dayOfWeek === 6) {
    dayOfWeekStr = 'Saturday'
  } else {
    dayOfWeekStr = 'Sunday'
  }

  if (dayOfWeekToDateStrMap[dayOfWeek]) {
    return `${dayOfWeekStr} - ${dayOfWeekToDateStrMap[dayOfWeek]}`
  } else {
    return dayOfWeekStr
  }
}

export function generateDowTableColumns(
  metricCode: string,
  metricDisplayType: string,
  startDate: moment.Moment,
  endDate: moment.Moment,
  dayOfWeek: number,
  selectedHours: number[],
): ITableColumn[] {
  const sortedSelectedHours = _.orderBy(selectedHours)

  if (metricDisplayType === 'price') {
    return [
      {
        key: `price_dow${dayOfWeek}`,
        title: getDayOfWeekLabel(dayOfWeek, startDate, endDate),
        align: 'center' as const,
        background:
          dayOfWeek % 2 === 0 || dayOfWeek === 7
            ? `${COLORS.Porcelain}`
            : `${COLORS.Salt}`,
        children: [
          ...(() => {
            const childrenColumns: IColumn[] = []
            sortedSelectedHours.forEach((hi) => {
              const hours = parseInt(
                hi.toString().padStart(4, '0').substring(0, 2),
                10,
              )
              const minutes = parseInt(
                hi.toString().padStart(4, '0').substring(2),
                10,
              )
              const totalMinutes = hours * 60 + minutes

              childrenColumns.push({
                key: `price_${dayOfWeek}_${totalMinutes}`,
                title: getHourLabel(hi),
                sortable: true,
                decimal: 2,
                background: {
                  colors: [
                    dayOfWeek % 2 === 0 || dayOfWeek === 7
                      ? `${COLORS.Porcelain}`
                      : `${COLORS.Salt}`,
                    'hsl(0, 80%, 70%)',
                    'hsl(120, 75%, 85%)',
                    'hsl(120, 60%, 55%)',
                  ],
                  range: [0, 100, 10000, 100000],
                  value: `<%- values.id === "summary" ? 0 : value === null ? 0 : (value < 100 ? 100 : value) %>`,
                },
              })
            })
            return childrenColumns
          })(),
        ],
      },
    ]
  } else if (metricDisplayType === 'number') {
    const checkCountHeatmapSetting = {
      colors: [
        dayOfWeek % 2 === 0 || dayOfWeek === 7
          ? `${COLORS.Porcelain}`
          : `${COLORS.Salt}`,
        'hsl(0, 80%, 70%)',
        'hsl(120, 75%, 85%)',
        'hsl(120, 60%, 55%)',
      ],
      range: [0, 10, 20, 100],
      value: `<%- values.id === "summary" ? 0 : value === null ? 0 : (value < 10 ? 10 : value) %>`,
    }

    const laborHourHeatmapSetting = {
      colors: [
        dayOfWeek % 2 === 0 || dayOfWeek === 7
          ? `${COLORS.Porcelain}`
          : `${COLORS.Salt}`,
        'hsl(120, 60%, 55%)',
        'hsl(120, 75%, 85%)',
        'hsl(0, 80%, 70%)',
      ],
      range: [0, 1, 5, 15],
      value: `<%- values.id === "summary" ? 0 : value === null ? 0 : (value < 1 ? 1 : value) %>`,
    }

    return [
      {
        key: `number_dow${dayOfWeek}`,
        title: getDayOfWeekLabel(dayOfWeek, startDate, endDate),
        align: 'center',
        background:
          dayOfWeek % 2 === 0 || dayOfWeek === 7
            ? `${COLORS.Porcelain}`
            : undefined,
        children: [
          ...(() => {
            const childrenColumns: IColumn[] = []
            sortedSelectedHours.forEach((hi) => {
              const hours = parseInt(
                hi.toString().padStart(4, '0').substring(0, 2),
                10,
              )
              const minutes = parseInt(
                hi.toString().padStart(4, '0').substring(2),
                10,
              )
              const totalMinutes = hours * 60 + minutes

              childrenColumns.push({
                key: `number_${dayOfWeek}_${totalMinutes}`,
                title: getHourLabel(hi),
                sortable: true,
                decimal: metricCode === 'average_labor_hour' ? 1 : 0,
                background:
                  metricCode === 'average_labor_hour'
                    ? laborHourHeatmapSetting
                    : checkCountHeatmapSetting,
              })
            })
            return childrenColumns
          })(),
        ],
      },
    ]
  } else if (metricDisplayType === 'percent') {
    const heatmap = {
      colors: [
        dayOfWeek % 2 === 0 ? `${COLORS.Porcelain}` : `${COLORS.Salt}`,
        'hsl(0, 80%, 70%)',
        'hsl(0, 100%, 65%)',
      ],
      range: [0, 100, 101],
      value: `<%-values.id === "summary" ? 0 : value === null ? 0 : (value > 100 ? 101 : value) %>`,
    }

    return [
      {
        key: `percent_dow${dayOfWeek}`,
        title: getDayOfWeekLabel(dayOfWeek, startDate, endDate),
        align: 'center',
        background: dayOfWeek % 2 === 0 ? `${COLORS.Porcelain}` : undefined,
        children: [
          ...(() => {
            const childrenColumns: IColumn[] = []
            sortedSelectedHours.forEach((hi) => {
              const hours = parseInt(
                hi.toString().padStart(4, '0').substring(0, 2),
                10,
              )
              const minutes = parseInt(
                hi.toString().padStart(4, '0').substring(2),
                10,
              )
              const totalMinutes = hours * 60 + minutes

              childrenColumns.push({
                key: `percent_${dayOfWeek}_${totalMinutes}`,
                title: getHourLabel(hi),
                sortable: true,
                decimal: 1,
                background: heatmap,
              })
            })

            return childrenColumns
          })(),
        ],
      },
    ]
  } else {
    return []
  }
}

export function generateDowCsvColumns(
  metricId: string,
  metricCode: string,
  metricDisplayType: string,
  startDate: moment.Moment,
  endDate: moment.Moment,
  dayOfWeek: number,
  selectedHours: number[],
) {
  const metricDisplayName =
    getRevenueCenterMetricDisplayName(metricId) || metricCode

  const sortedSelectedHours = _.orderBy(selectedHours)

  if (metricDisplayType === 'price') {
    const csvColumns: IColumn[] = []
    sortedSelectedHours.forEach((hi) => {
      const hours = parseInt(hi.toString().padStart(4, '0').substring(0, 2), 10)
      const minutes = parseInt(hi.toString().padStart(4, '0').substring(2), 10)
      const totalMinutes = hours * 60 + minutes

      csvColumns.push({
        key: `price_${dayOfWeek}_${totalMinutes}`,
        title: `${getDayOfWeekLabel(
          dayOfWeek,
          startDate,
          endDate,
        )} ${getHourLabel(hi)} - ${metricDisplayName}`,
        decimal: 2,
      })
    })
    return csvColumns
  } else if (metricDisplayType === 'number') {
    const csvColumns: IColumn[] = []
    sortedSelectedHours.forEach((hi) => {
      const hours = parseInt(hi.toString().padStart(4, '0').substring(0, 2), 10)
      const minutes = parseInt(hi.toString().padStart(4, '0').substring(2), 10)
      const totalMinutes = hours * 60 + minutes

      csvColumns.push({
        key: `number_${dayOfWeek}_${totalMinutes}`,
        title: `${getDayOfWeekLabel(
          dayOfWeek,
          startDate,
          endDate,
        )} ${getHourLabel(hi)} - ${metricDisplayName}`,
        decimal: metricCode === 'average_labor_hour' ? 1 : 0,
      })
    })
    return csvColumns
  } else if (metricDisplayType === 'percent') {
    const csvColumns: IColumn[] = []
    sortedSelectedHours.forEach((hi) => {
      const hours = parseInt(hi.toString().padStart(4, '0').substring(0, 2), 10)
      const minutes = parseInt(hi.toString().padStart(4, '0').substring(2), 10)
      const totalMinutes = hours * 60 + minutes

      csvColumns.push({
        key: `percent_${dayOfWeek}_${totalMinutes}`,
        title: `${getDayOfWeekLabel(
          dayOfWeek,
          startDate,
          endDate,
        )} ${getHourLabel(hi)} - ${metricDisplayName}`,
        decimal: 1,
      })
    })

    return csvColumns
  } else {
    return []
  }
}

function getSelectedDowWithExpectedSequence(selectedDayOfWeek: number[]) {
  const expectedDowSequence = [7, 1, 2, 3, 4, 5, 6]
  const selectedDowSet = new Set(selectedDayOfWeek)
  const selectedDowWithExpectedSequence: number[] = []

  expectedDowSequence.forEach((dow) => {
    if (selectedDowSet.has(dow)) {
      selectedDowWithExpectedSequence.push(dow)
    }
  })

  return selectedDowWithExpectedSequence
}

export function generateTableColumnsForSelectedDow(
  metricCode: string,
  metricDisplayType: string,
  startDate: moment.Moment,
  endDate: moment.Moment,
  selectedDayOfWeek: number[],
  selectedHours: number[],
) {
  const selectedDowWithExpectedSequence =
    getSelectedDowWithExpectedSequence(selectedDayOfWeek)

  let tableColumns: ITableColumn[] = []
  selectedDowWithExpectedSequence.forEach((dow: number) => {
    tableColumns = [
      ...tableColumns,
      ...generateDowTableColumns(
        metricCode,
        metricDisplayType,
        startDate,
        endDate,
        dow,
        selectedHours,
      ),
    ]
  })

  return tableColumns
}

export function generateCsvColumnsForSelectedDow(
  metricId: string,
  metricCode: string,
  metricDisplayType: string,
  startDate: moment.Moment,
  endDate: moment.Moment,
  selectedDayOfWeek: number[],
  selectedHours: number[],
) {
  const selectedDowWithExpectedSequence =
    getSelectedDowWithExpectedSequence(selectedDayOfWeek)

  let csvColumns: IColumn[] = []
  selectedDowWithExpectedSequence.forEach((dow: number) => {
    csvColumns = [
      ...csvColumns,
      ...generateDowCsvColumns(
        metricId,
        metricCode,
        metricDisplayType,
        startDate,
        endDate,
        dow,
        selectedHours,
      ),
    ]
  })

  return csvColumns
}
